// PPECB HP01 rev 8 §2.6-2.7: a pallet's inspection stays valid for 21 days on
// soft citrus and 28 days on hard citrus. An older inspection means the pallet
// is re-inspected before it ships. This module only WORKS OUT the age; nothing
// here blocks a load. Callers warn and save a reason (dispatch_warnings_ack).
//
// Soft citrus = runs.commodity 'SOFT CITRUS'. Lemons count as hard.
//
// Not counted: a pallet with no inspected_at gets kind 'none' ("no inspection
// date"), which is NOT the same as fresh. It carries no chip and is not listed
// as stale, because the missing date may just be an old row.

import { looksLikePalletOrBinNumber } from './coldroomFind'

const SOFT_LIMIT_DAYS = 21
const HARD_LIMIT_DAYS = 28
const AMBER_LEAD_DAYS = 3

export type InspectionAgeKind = 'none' | 'ok' | 'amber' | 'stale'

export interface InspectionAge {
  kind: InspectionAgeKind
  /** Whole days since the inspection; null when there is no date. */
  days: number | null
  limit: number
  soft: boolean
  /** Short chip text. Empty for 'ok'. */
  chip: string
  /** Full sentence for a title, a confirm line or a reason prompt. */
  detail: string
}

const DAY_MS = 86_400_000

export function isSoftCitrus(commodity: string | null | undefined): boolean {
  return (commodity ?? '').trim().toUpperCase() === 'SOFT CITRUS'
}

/** One sentence for every place that says a pallet is past its limit. A hand-approved pallet has no Re-inspection button, so it does not point there. */
export const STALE_ADVICE = 'Past the limit, so loading needs a reason. A pallet inside a PPECB consignment can be re-inspected with Re-inspection (203) on Inspection.'

export function inspectionAge(
  inspectedAt: string | null | undefined,
  commodity: string | null | undefined,
  now: number = Date.now(),
): InspectionAge {
  const soft = isSoftCitrus(commodity)
  const limit = soft ? SOFT_LIMIT_DAYS : HARD_LIMIT_DAYS
  const kindName = soft ? 'soft citrus' : 'hard citrus'
  const t = inspectedAt ? new Date(inspectedAt).getTime() : NaN
  if (!inspectedAt || Number.isNaN(t)) {
    return { kind: 'none', days: null, limit, soft, chip: '', detail: 'No inspection date on this pallet.' }
  }
  const days = Math.max(0, Math.floor((now - t) / DAY_MS))
  const base = `Inspected ${days} d ago, ${kindName} limit ${limit} d`
  if (days >= limit) {
    return { kind: 'stale', days, limit, soft,
      chip: `Inspected ${days} d ago`,
      detail: `${base}. ${STALE_ADVICE}` }
  }
  if (days >= limit - AMBER_LEAD_DAYS) {
    return { kind: 'amber', days, limit, soft,
      chip: `Inspected ${days} d ago`,
      detail: `${base}. Close to the limit.` }
  }
  return { kind: 'ok', days, limit, soft, chip: '', detail: base }
}

export interface AgedPallet {
  pallet_id: string
  inspected_at?: string | null
  commodity?: string | null
}

/** The pallets whose inspection is at or past the limit. */
export function stalePallets<T extends AgedPallet>(pallets: T[], now: number = Date.now()): { pallet: T; age: InspectionAge }[] {
  const out: { pallet: T; age: InspectionAge }[] = []
  for (const pallet of pallets) {
    const age = inspectionAge(pallet.inspected_at, pallet.commodity, now)
    if (age.kind === 'stale') out.push({ pallet, age })
  }
  return out
}

export interface DispatchWarning {
  code: string
  pallet_id: string
  text: string
}

export interface DispatchWarningsAck {
  by: string
  at: string
  warnings: DispatchWarning[]
  reason: string
}

export function staleWarnings(stale: { pallet: AgedPallet; age: InspectionAge }[]): DispatchWarning[] {
  return stale.map(s => ({ code: 'INSPECTION_AGE', pallet_id: s.pallet.pallet_id, text: s.age.detail }))
}

/** Merge new warnings into the ack already saved on the mission (the R-A2 to R-A5 checks share it). */
export function mergeAck(
  existing: DispatchWarningsAck | null | undefined,
  next: { by: string; at: string; warnings: DispatchWarning[]; reason: string },
): DispatchWarningsAck {
  const kept = (existing?.warnings ?? []).filter(w => !next.warnings.some(n => n.code === w.code && n.pallet_id === w.pallet_id))
  return {
    by: next.by,
    at: next.at,
    warnings: [...kept, ...next.warnings],
    reason: existing?.reason && existing.reason !== next.reason ? `${existing.reason}; ${next.reason}` : next.reason,
  }
}

type CommodityEmbed = { commodity?: string | null }

/** runs comes back from PostgREST as an object or a one-item array. */
export function embeddedCommodity(runs: CommodityEmbed | CommodityEmbed[] | null | undefined): string | null {
  const r = Array.isArray(runs) ? runs[0] : runs
  return r?.commodity ?? null
}

/** The PPECB inspection limit applies to export pallets only. */
export function inspectionAgeApplies(p: { channel?: string | null; target_market?: string | null }): boolean {
  return p.channel === 'EXPORT' || (p.channel == null && p.target_market != null)
}

// Reasons typed when scanning a stale pallet to load. Dispatch pre-fills its one confirm with them.
// In memory only: a reload loses them and Dispatch asks again.
const scanReasons = new Map<string, string[]>()
export function rememberScanReason(missionId: string, reason: string): void {
  const list = scanReasons.get(missionId) ?? []
  if (!list.includes(reason)) list.push(reason)
  scanReasons.set(missionId, list)
}
export function scanReasonsFor(missionId: string): string {
  return (scanReasons.get(missionId) ?? []).join('; ')
}
export function clearScanReasons(missionId: string): void {
  scanReasons.delete(missionId)
}

/** A reason that is really a scan. A wedge scanner types into the open prompt and sends Enter, so
 *  the next pallet's barcode would be saved as the reason. Refuse it. */
export const SCAN_AS_REASON_MESSAGE = 'That looks like a pallet scan. Type the reason.'
export function reasonLooksLikeScan(reason: string): boolean {
  return looksLikePalletOrBinNumber(reason)
}
